import React, { useState, useEffect } from 'react';
import './CreateJobAdvanced.scss';
import { createJobConfigHooks } from './hooks';
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Button,
  IconButton,
  Checkbox,
  FormGroup,
  Divider,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { JobDetailsCard } from './components/JobDetailsCard';
import { NotificationsCard } from './components/NotificationsCard';
import { ScheduleCard } from './components/ScheduleCard';

const DEFAULT_CONFIG = {
  kronos: {
    type: "1password_item",
    "1password_item_path": "Client credentials/UKG - EnjoyWurkAdmin - apiuser"
  },
  sftp: {
    type: "1password_item",
    "1password_item_path": "Client credentials/Empower 401k SFTP"
  },
  pgp: {
    type: "1password_item",
    "1password_item_path": "Client credentials/Empower PGP public key"
  },
  report: {},
  emailConfig: {
    emailContacts: []
  },
  includeLoans: true,
  includeContributionAmount8: true,
}

const { useJobGeneralConfig, useJobConfig } = createJobConfigHooks({partner: 'Empower', defaultConfig: DEFAULT_CONFIG});

const ClientPlanCard = () => {
  const [companyName, setCompanyName] = useJobConfig(["companyName"]);
  const [planNumber, setPlanNumber] = useJobConfig(["planNumber"]);
  return <Card>
    <CardHeader title="Client & Plan" subheader="Identifies which client this feed is for" />
    <CardContent>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            id="comp_short_name"
            label="Company short name"
            variant="standard"
            helperText="Internal client identifier"
            required
            value={companyName}
            onChange={e => setCompanyName(e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            id="plan_id"
            label="Plan Number"
            variant="standard"
            helperText="From Empower paperwork"
            required
            value={planNumber}
            onChange={e => setPlanNumber(e.target.value)}
          />
        </Grid>
      </Grid>
    </CardContent>
  </Card>
}

const SourceReportsCard = () => {
  const [report, setReport] = useJobConfig(["report"]);

  // There is some weird interaction when changing multiple fields, so I had to changing the whole object
  const mainReport = report.main;
  const setMainReport = (main) => setReport({...report, main})
  const ytd = report.ytd;
  const setYtd = (ytd) => setReport({...report, ytd, pay_period: undefined})
  const pp = report.pay_period;
  const setPP = (pay_period) => setReport({...report, ytd: undefined, pay_period})
  const scheduled = report.scheduled_deduction;
  const setScheduled = (scheduled_deduction) => setReport({...report, scheduled_deduction})

  const [mode, setMode] = useState(pp !== undefined ? "pp" : "ytd")
  useEffect(() => {
    if(mode === "ytd" && ytd === undefined) {
      setYtd("");
    } else if (mode === "pp" && pp === undefined) {
      setPP("");
    }
  }, [mode])

  return <Card>
    <CardHeader title="Source reports" subheader="Wurk saved report IDs that supply the feed data" />
    <CardContent>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField fullWidth id="main_report_id" label="Main report ID" variant="standard" helperText="e.g. 45674567" required value={mainReport} onChange={(e) => setMainReport(e.target.value)} />
        </Grid>
        {mode === "pp" ?
          <Grid item xs={6}>
            <TextField fullWidth id="pay_period_id" label="Pay Period report ID" variant="standard" helperText="e.g. 45674567" required value={pp || ""} onChange={(e) => setPP(e.target.value)} />
          </Grid>
          : null
        }
        {mode === "ytd" ?
          <Grid item xs={6}>
            <TextField fullWidth id="ytd_id" label="YTD report ID" variant="standard" helperText="e.g. 45674567" required value={ytd || ""} onChange={(e) => setYtd(e.target.value)} />
          </Grid>
          : null
        }
        <Grid item xs={6}>
          <TextField fullWidth id="sched_deductions_id" label="Scheduled deductions report ID" variant="standard" helperText="e.g. 45674567" required value={scheduled} onChange={(e) => setScheduled(e.target.value)} />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <FormLabel id="demo-radio-buttons-group-label">Reporting method</FormLabel>
            <RadioGroup
              aria-labelledby="demo-radio-buttons-group-label"
              defaultValue="female"
              name="radio-buttons-group"
              row
              value={mode}
              onChange={(e, value) => setMode(value)}
            >
              <FormControlLabel
                xs={6}
                sx={{
                  width: "calc(50% - 10px)"
                }}
                value="pp"
                control={<Radio />}
                label={<>
                  Pay period
                  <FormHelperText>This pay period only.</FormHelperText>
                </>}
              />
              
              <FormControlLabel
                xs={6}
                sx={{
                  width: "calc(50% - 10px)"
                }}
                
                value="ytd"
                control={<Radio />}
                label={<>
                  YTD
                  <FormHelperText>Year-to-date totals.</FormHelperText>
                </>}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
}


const DivisionMappingRow = ({k, v, changeKey, changeValue, deleteRow}) => {
  // Extracted this component so that we can have a little state here
  // and use onBlur
  // thus allowing writing repeated EIN names
  const [k_, setk] = useState(k)
  return (
    <TableRow>
      <TableCell>
        <TextField fullWidth label="EIN Name" variant="standard" helperText="e.g. CannDeliver" required value={k_} onChange={(e) => setk(e.target.value)} onBlur={() => changeKey(k, k_)} />
      </TableCell>
      <TableCell>
        <TextField fullWidth label="Division Code" variant="standard" helperText="e.g. P1" required value={v} onChange={(e) => changeValue(k, e.target.value)} />
      </TableCell>
      <TableCell>
        <IconButton onClick={() => deleteRow(k)}>
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  )
}

const DivisionMappingCard = () => {
  const [_mapping, setMapping] = useJobConfig(["divisionMapping"]);
  const mapping = _mapping || {};
  function addRow() {
    setMapping({...mapping, "": ""})
  }
  function deleteRow(key) {
    const newmapping = Object.fromEntries(
      Object.entries(mapping).filter(([k, v]) => k !== key)
    )
    setMapping(newmapping)
  }
  function changeKey(key, newKey) {
    const newmapping = Object.fromEntries(
      Object.entries(mapping).map(([k, v]) => {
        if(k == key) {
          return [newKey, v]
        } else {
          return [k, v]
        }
      })
    )
    setMapping(newmapping)
  }
  function changeValue(key, newValue) {
    const newmapping = Object.fromEntries(
      Object.entries(mapping).map(([k, v]) => {
        if(k == key) {
          return [k, newValue]
        } else {
          return [k, v]
        }
      })
    )
    setMapping(newmapping)
  }
  return <Card>
    <CardHeader title="Division mapping" subheader="Map each EIN to its Empower division code. Code comes from client's Empower paperwork." />
    <CardContent>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>EIN Name</TableCell>
            <TableCell>Division Code</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.keys(mapping).length > 0
            ? Object.entries(mapping).map(([k, v], i) => {
                return (
                  <DivisionMappingRow k={k} v={v} changeKey={changeKey} changeValue={changeValue} deleteRow={deleteRow} />
                )
              })
            :
            <TableRow>
              <TableCell colSpan={3}>
                No mapping configured
              </TableCell>
            </TableRow>
          }
        </TableBody>
      </Table>
      <Button startIcon={<AddIcon />} variant='outlined' onClick={addRow}>
        Add row
      </Button>
    </CardContent>
  </Card>
}

export const FileOptionsCard = () => {
  const [includeLoans, setIncludeLoans] = useJobConfig(["includeLoans"]);
  const [includeContributionAmount8, setIncludeContributionAmount8] = useJobConfig(["includeContributionAmount8"]);
  const [separateRoth401kCatchUp, setSeparateRoth401kCatchUp] = useJobConfig(["separateRoth401kCatchUp"]);
  const [_extraFieldMapping, setExtraFieldMapping] = useJobConfig(["extraFieldMapping"]);
  const extraFieldMapping = _extraFieldMapping || {};
  const contributionAmount8 = extraFieldMapping.contributionAmount8 || "";
  const setContributionAmount8 = (value) => {
    const { contributionAmount8, ...rest } = extraFieldMapping;
    setExtraFieldMapping(value ? {...rest, contributionAmount8: value} : rest);
  };
  const contributionAmount8Checked = includeContributionAmount8 !== false && Boolean(includeContributionAmount8 || contributionAmount8);

  return <Card>
    <CardHeader title="File Options" subheader="Optional fields specific to this client's setup with Empower." />
    <CardContent>
      <FormGroup>
        <FormControlLabel
          control={<Checkbox />}
          checked={Boolean(includeLoans)}
          onChange={(e, checked) => setIncludeLoans(checked)}
          label={<>
            Include 401(k) loan fields
            <FormHelperText>Included by default on new jobs. Uncheck only if this client should not receive a loan column.</FormHelperText>
          </>}
        />
        <Divider sx={{margin: "10px"}} />
        <FormControlLabel
          control={<Checkbox />}
          checked={separateRoth401kCatchUp}
          onChange={(e, checked) => setSeparateRoth401kCatchUp(checked)}
          label={<>
            Separate Roth Catch-Up from Roth 401(k)
            <FormHelperText>By default, Roth Catch-Up rolls into the Roth 401(k) column. Check this to keep them separated.</FormHelperText>
          </>}
        />
        <Divider sx={{margin: "10px"}} />
        <FormControlLabel
          control={<Checkbox />}
          checked={contributionAmount8Checked}
          onChange={(e, checked) => setIncludeContributionAmount8(checked)}
          label={<>
            Include Contribution Amount 8 field
            <FormHelperText>Included by default on new jobs. Uncheck to omit the column.</FormHelperText>
          </>}
        />
        <TextField
          fullWidth
          id="contribution_amount_8"
          label="Contribution Amount 8 field mapping"
          variant="standard"
          helperText="Optional. Main report column to copy into Contribution Amount 8. Leave blank to include the column empty."
          value={contributionAmount8}
          onChange={e => setContributionAmount8(e.target.value)}
          disabled={!contributionAmount8Checked}
        />
      </FormGroup>
    </CardContent>
  </Card>
}

const AdvancedSettingsCard = () => {
  const [fileNamingSchema, setFileNamingSchema] = useJobConfig(["fileNamingSchema"]);
  const [planNumber, _setPlanNumber] = useJobConfig(["planNumber"]);

  const {test, setTest} = (function () {
    const parts = (fileNamingSchema || "").split("/");
    const filename = parts[parts.length - 1];

    const test = filename.startsWith("Test_");
    const setTest = (v) => {
      if((test && v) || (!test && !v)) {
        return
      }
      const prefix = parts.slice(0, parts.length-1)
      if(v) {
        const newFileNamingSchema = [...prefix, "Test_" + filename].join("/");
        setFileNamingSchema(newFileNamingSchema);
      } else {
        const newFileNamingSchema = [...prefix, filename.slice("Test_".length)].join("/");
        setFileNamingSchema(newFileNamingSchema);
      }
    }
    return {test, setTest}
  }())

  const defaultNoTestFileNamingSchema = `/Home/wurk/To_Empower/ftp.${planNumber}.{YEAR_4_DIGITS}{MONTH_LEADING_ZERO}{DAY_LEADING_ZERO}.{HOUR_24}{MINUTE}{SECOND}.csv.pgp`
  const defaultTestFileNamingSchema = `/Home/wurk/To_Empower/Test_ftp.${planNumber}.{YEAR_4_DIGITS}{MONTH_LEADING_ZERO}{DAY_LEADING_ZERO}.{HOUR_24}{MINUTE}{SECOND}.csv.pgp`
  const defaultFileNamingSchema = test ? defaultTestFileNamingSchema : defaultNoTestFileNamingSchema;

  const [custom, setCustom] = useState(fileNamingSchema !== defaultFileNamingSchema && fileNamingSchema !== undefined);

  useEffect(() => {
    // If we set the file naming schema to custom, we reset it to clean out whatever bad schema we had there
    if(custom) {
      setFileNamingSchema(defaultFileNamingSchema)
    }
  }, [custom])

  useEffect(() => {
    // If we change the plan number
    // and we don't have a custom file naming schema
    // we reset the file naming schema to ensure it includes the last plan number
    if(!custom) {
      setFileNamingSchema(defaultFileNamingSchema)
    }
  }, [planNumber])

  return <Card>
    <CardHeader title="Advanced Settings" subheader="Rarely needed" />
    <CardContent>
      <FormGroup>
        <FormControlLabel
          control={<Checkbox defaultChecked />}
          checked={test}
          onChange={(e, checked) => setTest(checked)}
          label={<>
            Test file
            <FormHelperText>Prepends "Test_" to the filename so the vendor knows this is a test run, not production data.</FormHelperText>
            <Card sx={{padding: "10px", backgroundColor: "#eee", color: "#666"}}>
              <FormHelperText>
                Filename preview:
              </FormHelperText>
              <FormHelperText>
                {fileNamingSchema}
              </FormHelperText>
            </Card>

          </>}
        />
        <Divider sx={{margin: "10px"}} />
        <FormControlLabel
          control={<Checkbox defaultChecked />}
          checked={custom}
          onChange={(e, checked) => setCustom(checked)}
          label={<>
            Override file naming schema
            <FormHelperText>Configure a custom file naming schema for this job. It should not be needed unless Empower requests it.</FormHelperText>
          </>}
        />
        <TextField
          fullWidth
          id="plan_id"
          label="File naming schema"
          variant="standard"
          helperText="Default empower naming pattern is used unless overridden."
          value={fileNamingSchema}
          onChange={e => setFileNamingSchema(e.target.value)}
          disabled={!custom}
        />
      </FormGroup>
    </CardContent>
  </Card>
}


export const EmpowerPayrollExportForm = () => {
  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <JobDetailsCard useJobGeneralConfig={useJobGeneralConfig} />
    </Grid>
    <Grid item xs={12}>
      <ClientPlanCard />
    </Grid>
    <Grid item xs={12}>
      <SourceReportsCard />
    </Grid>
    <Grid item xs={12}>
      <DivisionMappingCard />
    </Grid>
    <Grid item xs={12}>
      <FileOptionsCard />
    </Grid>
    <Grid item xs={12}>
      <NotificationsCard useJobConfig={useJobConfig} />
    </Grid>
    <Grid item xs={12}>
      <ScheduleCard useJobGeneralConfig={useJobGeneralConfig} />
    </Grid>
    <Grid item xs={12}>
      <AdvancedSettingsCard />
    </Grid>
  </Grid>
}
